import { useEffect, useId, useState, type FormEvent } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router";
import {
  Activity,
  BadgeCheck,
  BarChart3,
  Building2,
  ClipboardList,
  Home,
  Languages,
  Menu,
  MessageSquareWarning,
  MoonStar,
  ScrollText,
  Search,
  ShieldCheck,
  SunMedium,
  TicketPercent,
  UserX,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from "lucide-react";
import { AppUpdateBanner } from "../../components/AppUpdateBanner";
import { useAuth } from "../../lib/auth";
import { useAppPreferences } from "../../lib/preferences";
import { consoleApi, type ConsoleHomeResponse } from "../../lib/admin-console-api";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  badge?: (home: ConsoleHomeResponse | null) => number;
  end?: boolean;
}

const queueCount = (home: ConsoleHomeResponse | null, keys: string[]) =>
  home?.queues.filter((queue) => keys.includes(queue.key)).reduce((sum, queue) => sum + queue.count, 0) ?? 0;

export const NAV_GROUPS: Array<{ label: string; items: NavItem[] }> = [
  {
    label: "Work",
    items: [
      { to: "/admin", label: "Home", icon: Home, end: true },
      { to: "/admin/activity", label: "Bookings & sessions", icon: Activity },
      {
        to: "/admin/approvals",
        label: "Approvals",
        icon: BadgeCheck,
        badge: (home) => queueCount(home, ["provider_registrations", "venue_additions", "venue_removals", "tournaments"]),
      },
      { to: "/admin/moderation", label: "Moderation", icon: MessageSquareWarning, badge: (home) => queueCount(home, ["reviews", "reports"]) },
      { to: "/admin/deletion-requests", label: "Deletion requests", icon: UserX, badge: (home) => queueCount(home, ["deletion_requests"]) },
    ],
  },
  {
    label: "Records",
    items: [
      { to: "/admin/providers", label: "Providers", icon: ClipboardList },
      { to: "/admin/venues", label: "Venues", icon: Building2 },
      { to: "/admin/accounts", label: "Accounts", icon: Users },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/admin/settlements", label: "Settlements", icon: Wallet, adminOnly: true, badge: (home) => home?.overdue_settlements ?? 0 },
      { to: "/admin/promotions", label: "Booking campaigns", icon: TicketPercent },
      { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/admin/audit", label: "Audit log", icon: ScrollText },
      { to: "/admin/team", label: "Team", icon: ShieldCheck, adminOnly: true },
    ],
  },
];

export function AdminConsoleLayout() {
  const { signOut, user } = useAuth();
  const { language, setLanguage, setThemePreference, t, themePreference } = useAppPreferences();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [home, setHome] = useState<ConsoleHomeResponse | null>(null);
  const [query, setQuery] = useState(location.pathname === "/admin/search" ? searchParams.get("q") ?? "" : "");
  const searchId = useId();
  const isAdmin = user?.role === "admin";

  // Queue counts refresh on every navigation, so badges follow the work.
  useEffect(() => {
    let cancelled = false;
    consoleApi
      .home()
      .then((value) => {
        if (!cancelled) setHome(value);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => setMenuOpen(false), [location.pathname, location.search]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = query.trim();
    if (value.length >= 2) navigate(`/admin/search?q=${encodeURIComponent(value)}`);
  };

  const nextLanguage = language === "ar" ? "en" : "ar";
  const nextTheme = themePreference === "dark" ? "light" : "dark";
  const ThemeIcon = themePreference === "dark" ? SunMedium : MoonStar;

  const navigation = (
    <nav aria-label={t("Admin sections")} className="console-nav">
      {NAV_GROUPS.map((group) => {
        const items = group.items.filter((item) => isAdmin || !item.adminOnly);
        return (
          <div className="console-nav-group" key={group.label}>
            <p className="console-nav-heading">{t(group.label)}</p>
            {items.map((item) => {
              const Icon = item.icon;
              const count = item.badge?.(home) ?? 0;
              return (
                <NavLink className="console-nav-link" end={item.end} key={item.to} to={item.to}>
                  <Icon aria-hidden="true" className="console-nav-icon" />
                  <span className="console-nav-label">{t(item.label)}</span>
                  {count > 0 ? (
                    <span className="console-nav-badge">
                      <span aria-hidden="true">{count > 99 ? "99+" : count}</span>
                      <span className="sr-only">{t("{count} waiting", { count })}</span>
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </nav>
  );

  const accountControls = (
    <div className="console-account">
      <div className="min-w-0">
        <p className="console-account-name">{user?.display_name ?? user?.email}</p>
        <p className="console-muted">{isAdmin ? t("Admin") : t("Support")}</p>
      </div>
      <div className="console-account-actions">
        <button
          aria-label={nextLanguage === "ar" ? t("Switch to Arabic") : t("Switch to English")}
          className="ops-button"
          data-size="sm"
          data-variant="secondary"
          onClick={() => setLanguage(nextLanguage)}
          type="button"
        >
          <Languages aria-hidden="true" size={15} />
          {nextLanguage === "ar" ? "العربية" : "English"}
        </button>
        <button
          aria-label={nextTheme === "light" ? t("Switch to light mode") : t("Switch to dark mode")}
          className="ops-button"
          data-size="sm"
          data-variant="secondary"
          onClick={() => setThemePreference(nextTheme)}
          type="button"
        >
          <ThemeIcon aria-hidden="true" size={15} />
          {nextTheme === "light" ? t("Light") : t("Dark")}
        </button>
        <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => void signOut()} type="button">
          {t("Sign out")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="console-shell">
      <aside className="console-sidebar" aria-label={t("Admin console")}>
        <div className="console-brand">
          <span className="console-brand-mark" aria-hidden="true">g</span>
          <div>
            <p className="console-brand-name">gaamed</p>
            <p className="console-muted">{t("Operations console")}</p>
          </div>
        </div>
        {navigation}
        {accountControls}
      </aside>

      <div className="console-body">
        <header className="console-topbar">
          <button
            aria-expanded={menuOpen}
            aria-label={menuOpen ? t("Close menu") : t("Open menu")}
            className="console-menu-button ops-button"
            data-size="sm"
            data-variant="secondary"
            onClick={() => setMenuOpen((open) => !open)}
            type="button"
          >
            {menuOpen ? <X aria-hidden="true" size={18} /> : <Menu aria-hidden="true" size={18} />}
          </button>
          <form className="console-search" onSubmit={submitSearch} role="search">
            <label className="sr-only" htmlFor={searchId}>{t("Search providers, venues and accounts")}</label>
            <Search aria-hidden="true" className="console-search-icon" size={16} />
            <input
              className="ops-input console-search-input"
              id={searchId}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("Search providers, venues, emails or phones")}
              type="search"
              value={query}
            />
          </form>
        </header>

        {menuOpen ? (
          <div className="console-mobile-menu">
            {navigation}
            {accountControls}
          </div>
        ) : null}

        <main className="console-main" id="admin-main">
          <Outlet />
          <AppUpdateBanner />
        </main>
      </div>
    </div>
  );
}
